import {
  Box,
  Button,
  FlexBox,
  Icon,
  IconButton,
  InputText,
  Switch,
  Text,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { ampli } from 'lib-shared-tracking';
import debounce from 'lodash/debounce';
import {
  ChangeEvent,
  KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { useBreakpointValue } from 'utils/mediaQuery';
import { useAppSelector } from 'utils/redux/useAppSelector';

import styles from './searchInput.module.scss';

type SearchInputProps = {
  onSearch: (query: string, strict: boolean) => void;
  onClear: VoidFunction;
  onSavedSearchClear: VoidFunction;
};

/** Represents the input component that is used for typing search queries. */
export const SearchInput = ({
  onSearch,
  onClear,
  onSavedSearchClear,
}: SearchInputProps): JSX.Element => {
  const { params, savedSearch } = useAppSelector((state) => state.search);
  const [query, setQuery] = useState(params.query ?? '');
  const [strict, setStrict] = useState(params.strict ?? false);

  const isSmallScreen = useBreakpointValue({ base: true, sm: false });

  useEffect(() => {
    setQuery(params.query ?? '');
    setStrict(params.strict ?? false);
  }, [params.query, params.strict]);

  const handleChangeQuery = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
  }, []);

  const handleChangeStrict = useCallback(() => {
    const strictNext = !strict;

    setStrict(strictNext);
    ampli.searchStrictToggled({ value: strictNext });
    query && onSearch(query, strictNext);
  }, [onSearch, query, strict]);

  const debouncedOnSearch = useMemo(
    () => debounce(onSearch, 500, { leading: true }),
    [onSearch],
  );

  const handleSubmitQuery = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        debouncedOnSearch(query, strict);
      }
    },
    [debouncedOnSearch, query, strict],
  );

  const handleFocus = useCallback(
    () => ampli.searchFieldFocused({ location: 'searchPage' }),
    [],
  );

  const handleBlur = useCallback(
    () => ampli.searchFieldBlurred({ location: 'searchPage' }),
    [],
  );

  const placeholder = useMemo(() => {
    switch (params.scope) {
      case 'conversations':
        return 'Search conversations by ID, subject, messages, internal notes, assigned agent and more...';
      case 'users':
        return 'Search contacts by name, email address, phone number and more...';
      case 'companies':
        return 'Search companies by name...';
    }
  }, [params.scope]);

  return (
    <div className={styles.searchContainer}>
      <InputText
        name="search"
        type="text"
        value={query}
        placeholder={placeholder}
        onChange={handleChangeQuery}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleSubmitQuery}
        prefix={
          savedSearch && {
            type: 'custom',
            value: (
              <FlexBox alignItems="center">
                <Text.Paragraph variant="2">
                  <span data-testid="saved-search-title">
                    {savedSearch.name}
                  </span>
                </Text.Paragraph>

                <IconButton
                  variant="tertiary"
                  icon="close"
                  onClick={onSavedSearchClear}
                />
              </FlexBox>
            ),
          }
        }
        suffix={{
          type: 'custom',
          noWrap: true,
          value: (
            <FlexBox alignItems="center">
              {query && (
                <Button
                  variant="tertiary"
                  onClick={onClear}
                  className={styles.clearButton}
                >
                  Clear
                </Button>
              )}

              <FlexBox
                alignItems="center"
                justifyContent="flex-end"
                as={isSmallScreen ? 'div' : 'label'}
                gap="200"
              >
                {!isSmallScreen && (
                  <Text.Paragraph
                    variant="3"
                    subdued
                    className={styles.exactMatchLabel}
                  >
                    Show exact matches only
                  </Text.Paragraph>
                )}

                <Tooltip
                  content={
                    <Box width="160px">
                      Use this when searching for names, emails or specific
                      phrases.
                    </Box>
                  }
                  requireClick={!!isSmallScreen}
                  side="left"
                  wrap="div"
                  wrapProps={{ style: { display: 'flex' } }}
                >
                  <Icon name="info" />
                </Tooltip>

                <Switch
                  name=""
                  value=""
                  checked={strict}
                  onChange={handleChangeStrict}
                />
              </FlexBox>
            </FlexBox>
          ),
        }}
      />
    </div>
  );
};
